import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, X } from "lucide-react";
import { useState } from "react";

/**
 * VAULT INSIGHT — reusable educational nudge card.
 *
 * Props: insight object (see /data/vault_insights.js).
 *   variant: "editorial" (public marketing) | "compact" (in-app)
 *   dismissible: boolean — when true, exposes a close button that
 *   remembers the dismissal in localStorage under `vault.insight.<id>`.
 */
export default function VaultInsight({ insight, variant = "editorial", dismissible = false }) {
    const key = `vault.insight.${insight?.id}`;
    const [hidden, setHidden] = useState(() => {
        try { return dismissible && localStorage.getItem(key) === "1"; }
        catch { return false; }
    });

    if (!insight || hidden) return null;

    const dismiss = () => {
        try { localStorage.setItem(key, "1"); } catch { /* noop */ }
        setHidden(true);
    };

    return (
        <aside
            className={`vi-root vi-root--${variant}`}
            data-testid={`vault-insight-${insight.id}`}
            aria-label="VAULT Insight"
        >
            <style>{VI_CSS}</style>
            <div className="vi-eyebrow">
                <Sparkles size={12} strokeWidth={2.2}/> VAULT INSIGHT
                <span className="vi-time">· {insight.reading_time}</span>
            </div>

            <p className="vi-quote">&ldquo;{insight.insight}&rdquo;</p>

            {variant === "editorial" && insight.why_it_matters && (
                <p className="vi-why">
                    <span className="vi-why__label">Why it matters — </span>
                    {insight.why_it_matters}
                </p>
            )}

            <div className="vi-foot">
                {insight.read_more_url && (
                    <Link to={insight.read_more_url} className="vi-cta" data-testid={`vi-cta-${insight.id}`}>
                        {insight.cta || "Read more"} <ArrowRight size={12}/>
                    </Link>
                )}
                {dismissible && (
                    <button type="button" onClick={dismiss} aria-label="Dismiss insight"
                            className="vi-dismiss" data-testid={`vi-dismiss-${insight.id}`}>
                        <X size={13}/>
                    </button>
                )}
            </div>
        </aside>
    );
}

const VI_CSS = `
.vi-root { position: relative; padding: 22px 24px; border-radius: 20px; margin: 24px 0; font-family: "IBM Plex Sans", ui-sans-serif, system-ui, sans-serif; }
.vi-root--editorial { background: linear-gradient(140deg, #FBF7EE 0%, #F3EBD3 100%); border: 1px solid rgba(198,168,107,0.32); }
.vi-root--compact { background: rgba(198,168,107,0.08); border: 1px solid rgba(198,168,107,0.28); padding: 14px 18px; margin: 12px 0; }
.vi-eyebrow { display: inline-flex; align-items: center; gap: 6px; font-size: 10.5px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #8f6f38; margin-bottom: 12px; }
.vi-time { color: rgba(120,113,108,0.9); font-weight: 500; letter-spacing: 0.06em; }
.vi-quote { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 22px; font-weight: 500; letter-spacing: -0.012em; line-height: 1.28; color: #0F1E3A; margin: 0 0 12px; }
.vi-root--compact .vi-quote { font-size: 17px; margin-bottom: 6px; }
.vi-why { font-size: 14px; line-height: 1.55; color: #57534E; margin: 0 0 16px; }
.vi-why__label { font-weight: 600; color: #0F1E3A; }
.vi-foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.vi-cta { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; color: #0F1E3A; text-decoration: none; }
.vi-cta:hover { color: #C6A86B; }
.vi-dismiss { background: transparent; border: none; color: #78716C; cursor: pointer; padding: 6px; border-radius: 999px; transition: background-color 160ms cubic-bezier(0.16,1,0.3,1); }
.vi-dismiss:hover { background: rgba(15,30,58,0.08); color: #0F1E3A; }
`;
